import type { FileUploaderItem } from '@plaidev/sour-react';
import { Button, FileUploaderDragDrop, RadioGroup, RadioItem, Select } from '@plaidev/sour-react';
import { type ReactElement, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { CSV_ANALYZE_STREAM_MODEL_OPTIONS, type CsvAnalyzeStreamModelId } from 'troppus-common/shared';

import type { FormValues } from '../types';

type UploadStepProps = {
  sourceSystemSelectOptions: { value: string; label: string }[];
  sourceSystemId: string;
  uploadItems: FileUploaderItem[];
  onSelectFiles: (files: FileList) => void;
  onDeleteUpload: (fileId: string) => void;
  uploadMutationIsPending: boolean;
  uploadProgress: number | null;
  selectedFile: File | null;
  onStartUpload: () => void;
  navigateToList: () => void;
  isSystemAdmin: boolean;
  analyzeModelId: CsvAnalyzeStreamModelId;
  onAnalyzeModelIdChange: (id: CsvAnalyzeStreamModelId) => void;
};

export function UploadStep({
  sourceSystemSelectOptions,
  sourceSystemId,
  uploadItems,
  onSelectFiles,
  onDeleteUpload,
  uploadMutationIsPending,
  uploadProgress,
  selectedFile,
  onStartUpload,
  navigateToList,
  isSystemAdmin,
  analyzeModelId,
  onAnalyzeModelIdChange,
}: UploadStepProps): ReactElement {
  const { control } = useFormContext<FormValues>();
  const fileUploaderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const input = fileUploaderRef.current?.querySelector<HTMLInputElement>('input[type="file"]');
    input?.focus();
  }, []);

  return (
    <>
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-default">
          ソースシステム<span className="text-negative">*</span>
        </h2>
        <Controller
          control={control}
          name="sourceSystemId"
          render={({ field }) => (
            <Select
              variant="contained"
              searchable={false}
              placeholder="選択してください"
              options={sourceSystemSelectOptions}
              value={field.value || undefined}
              onChange={(v) => field.onChange(v ?? '')}
            />
          )}
        />
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-default">CSV アップロード</h2>
        <p className="text-sm text-muted-light">ファイルをドラッグ＆ドロップするか選択し、「アップロード開始」を押してください。</p>
        <FileUploaderDragDrop
          ref={fileUploaderRef}
          accept=".csv,text/csv"
          list={uploadItems}
          uploadFile={onSelectFiles}
          deleteFile={onDeleteUpload}
        />
      </section>

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="primary"
            loading={uploadMutationIsPending}
            disabled={!sourceSystemId || !selectedFile}
            onClick={onStartUpload}
          >
            アップロード開始
          </Button>
          <Button type="button" variant="basic" disabled={uploadMutationIsPending} onClick={navigateToList}>
            キャンセル
          </Button>
        </div>
        {uploadMutationIsPending && uploadProgress !== null ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between text-xs text-muted-light">
              <span>{uploadProgress < 1 ? 'アップロード中…' : 'アップロード完了、解析を開始します…'}</span>
              <span>{Math.floor(uploadProgress * 100)}%</span>
            </div>
            <div
              className="h-2 overflow-hidden rounded-full bg-subtle"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.floor(uploadProgress * 100)}
              aria-label="アップロード進捗"
            >
              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${Math.floor(uploadProgress * 100)}%` }} />
            </div>
          </div>
        ) : null}
      </div>

      {isSystemAdmin ? (
        <section className="admin-ui flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-default">列推定モデル</h2>
          <p className="text-sm text-muted-light">
            LLM による CSV カラム意味推定に使用するモデルを選びます（システム管理者のみ表示）。「アップロード開始」の前に選んでください。
          </p>
          <RadioGroup value={analyzeModelId} onValueChange={(v) => onAnalyzeModelIdChange(v as CsvAnalyzeStreamModelId)}>
            {CSV_ANALYZE_STREAM_MODEL_OPTIONS.map((o) => (
              <RadioItem key={o.value} value={o.value} label={o.label} />
            ))}
          </RadioGroup>
        </section>
      ) : null}
    </>
  );
}
